import React, { useEffect, useState } from "react";
import { api, STATUS_OPTIONS, TASK_TYPES } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ExternalLink, Search } from "lucide-react";
import { Input } from "../components/ui/input";

const STATUS_LABEL = STATUS_OPTIONS.reduce((acc, s) => { acc[s.value] = s.label; return acc; }, {});
const TYPE_LABEL = TASK_TYPES.reduce((acc, s) => { acc[s.value] = s.label; return acc; }, {});

export default function Backlog() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [clientFilter, setClientFilter] = useState("all");

  const load = async () => {
    const [t, u, c] = await Promise.all([
      api.get("/tasks/backlog"),
      api.get("/users"),
      api.get("/clients"),
    ]);
    setTasks(t.data);
    setUsers(u.data);
    setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const findUser = (id) => users.find((u) => u.user_id === id);
  const findClient = (id) => clients.find((c) => c.client_id === id);

  const filtered = tasks.filter((t) => {
    if (clientFilter !== "all" && t.client_id !== clientFilter) return false;
    if (q && !t.title.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="backlog-page">
      <PageHeader
        eyebrow="Archive"
        title="Backlog"
        description="Every published task lives here forever — full timeline, refs, and links. Admin-only."
      />
      <div className="px-6 lg:px-10 py-8 space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title…"
              data-testid="backlog-search"
              className="pl-9 rounded-none bg-neutral-900 border-neutral-800 h-9 text-sm"
            />
          </div>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            data-testid="backlog-client-filter"
            className="bg-neutral-900 border border-neutral-800 rounded-none h-9 text-xs px-2 text-white"
          >
            <option value="all">All clients</option>
            {clients.map((c) => (
              <option key={c.client_id} value={c.client_id}>{c.name}</option>
            ))}
          </select>
          <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            {filtered.length} item{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="border border-neutral-900">
          <div className="px-5 py-4 border-b border-neutral-900 grid grid-cols-12 gap-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            <div className="col-span-4">Title</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Client</div>
            <div className="col-span-2">Team</div>
            <div className="col-span-1">Published</div>
            <div className="col-span-1 text-right">Refs</div>
          </div>
          {filtered.length === 0 && (
            <div className="px-5 py-12 text-center text-sm text-neutral-500">
              Nothing posted yet. Once you mark a task as <span className="text-neutral-300">Posted</span> on Schedule, it lands here.
            </div>
          )}
          {filtered.map((t) => {
            const editor = findUser(t.assigned_editor_id);
            const smm = findUser(t.assigned_smm_id);
            const cli = findClient(t.client_id);
            return (
              <div
                key={t.task_id}
                className="px-5 py-4 grid grid-cols-12 gap-3 border-b border-neutral-900 hover:bg-neutral-900/40 transition-colors text-sm"
                data-testid={`backlog-row-${t.task_id}`}
              >
                <div className="col-span-4">
                  <div className="font-medium">{t.title}</div>
                  {t.notes && <div className="text-xs text-neutral-500 line-clamp-1 mt-0.5">{t.notes}</div>}
                </div>
                <div className="col-span-2 text-neutral-400 text-xs uppercase tracking-[0.15em]">
                  {TYPE_LABEL[t.task_type] || t.task_type || "—"}
                </div>
                <div className="col-span-2 text-neutral-300">{cli?.name || "—"}</div>
                <div className="col-span-2 text-neutral-400 text-xs">
                  {editor?.name && <div>✏️ {editor.name}</div>}
                  {smm?.name && <div>📣 {smm.name}</div>}
                </div>
                <div className="col-span-1 text-xs font-mono text-neutral-400">
                  {t.updated_at?.slice(0, 10)}
                </div>
                <div className="col-span-1 flex justify-end gap-2">
                  {t.ref_video_link && (
                    <a href={t.ref_video_link} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-[#00C97A]" title="Reference">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {t.published_url && (
                    <a href={t.published_url} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300" title="Published">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
